import { useAudio } from '@providers'
import classNames from 'classnames'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon, Text } from '@ds'
import { LocalStorageService } from '@services'
import {
  MAX_AVAILABLE_PAGE,
  QUEST_ITEMS_PER_PAGE,
  useAvailableQuestsActions,
  useAvailableQuestsState,
  usePageQuestState,
} from '@store'

import styles from './SpiralMove.module.scss'

export const SpiralMove = () => {
  const { t } = useTranslation()

  const pages = usePageQuestState()
  const { setPages } = useAvailableQuestsActions()
  const availableQuests = useAvailableQuestsState()
  const { playSmartClick } = useAudio()

  const canMoveToNextPage = useMemo(() => {
    if (pages.current >= MAX_AVAILABLE_PAGE) return false

    const startIndex = pages.current * QUEST_ITEMS_PER_PAGE
    const endIndex = startIndex + QUEST_ITEMS_PER_PAGE

    const sceneQuests = (availableQuests || [])
      .filter((q) => q.type === 'major')
      .slice(startIndex, endIndex)

    if (sceneQuests.length < QUEST_ITEMS_PER_PAGE) return false

    const allSolved = sceneQuests.every((q) => q.isSolved)
    return allSolved
  }, [availableQuests, pages.current])

  // TODO: Can use if we will have arrow animation design
  // const shouldMoveToNextPage = useMemo(() => {
  //   if (!canMoveToNextPage) return false;
  //   const nextAvailableQuestIndex = availableQuests.filter(q => q.type === 'major').findLastIndex(q => q.isAvailable);
  //   const nextAvailablePage = Math.max(0, Math.ceil(nextAvailableQuestIndex / QUEST_ITEMS_PER_PAGE) - 1);
  //   return nextAvailablePage > pages.current;
  // },[pages.current, availableQuests, canMoveToNextPage])

  const handleArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      playSmartClick()
      const newPage =
        direction === 'right'
          ? Math.min(pages.current + 1, MAX_AVAILABLE_PAGE)
          : Math.max(pages.current - 1, 0)

      setPages({ current: newPage, total: pages.total })
    },
    [pages, playSmartClick, setPages, canMoveToNextPage]
  )

  const HINT_KEY = 'spiralRightArrowHint'
  const wasShown = LocalStorageService.getItem<boolean>(HINT_KEY)
  const shouldShowHintUnder = canMoveToNextPage && !wasShown

  return (
    <div className={styles.root}>
      <Icon
        name="longLeftArrow"
        size="L"
        className={classNames(styles.arrowIcon, {
          [styles.invisible]: pages.current === 0,
        })}
        onClick={() => handleArrowClick('left')}
      />
      <Text type="ghost" className={styles.text} noWrap>
        {t('home.moveSpiral')}
      </Text>
      <div className={styles.arrowWrapper}>
        {canMoveToNextPage && (
          <Icon
            name="longRightArrow"
            size="L"
            className={classNames(styles.arrowIcon, {
              [styles.invisible]: !canMoveToNextPage,
              [styles.hint]: canMoveToNextPage,
            })}
            onClick={() => {
              if (shouldShowHintUnder) {
                LocalStorageService.setItem(HINT_KEY, true)
              }
              handleArrowClick('right')
            }}
          />
        )}
        {!canMoveToNextPage && pages.current < MAX_AVAILABLE_PAGE && (
          <Icon
            name="longRightArrow"
            size="L"
            className={styles.arrowIcon}
            onClick={() => {
              if (shouldShowHintUnder) {
                LocalStorageService.setItem(HINT_KEY, true)
              }
              handleArrowClick('right')
            }}
          />
        )}
        {shouldShowHintUnder && (
          <div
            className={styles.hintUnder}
            onClick={() => {
              LocalStorageService.setItem(HINT_KEY, true)
              handleArrowClick('right')
            }}
          >
            {t('home.moveSpiral')}
          </div>
        )}
      </div>
    </div>
  )
}
